import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/delivery.scss";
import "../css/terms.scss";

function Terms() {
  return (
    <main className="policy-page policy-page--terms">
      <header className="policy-hero">
        <div>
          <p className="policy-eyebrow">Information · {siteConfig.brandName}</p>
          <h1>Terms of use</h1>
        </div>
        <div className="policy-hero__intro">
          <p>
            The rules for exploring this demonstration storefront, with clear
            boundaries around its non-transactional checkout experience.
          </p>
          <span>Last updated 13 August 2026</span>
        </div>
      </header>

      <div className="policy-shell">
        <aside className="policy-sidebar">
          <nav aria-label="Customer policies">
            <p>Customer policies</p>
            <Link to="/shipping">Shipping &amp; delivery</Link>
            <Link to="/returns">Returns &amp; refunds</Link>
            <Link to="/privacy">Privacy</Link>
            <Link className="is-active" to="/terms" aria-current="page">
              Terms
            </Link>
          </nav>
          <nav className="policy-toc" aria-label="On this page">
            <p>On this page</p>
            <a href="#terms-demo">Demo status</a>
            <a href="#terms-use">Using the site</a>
            <a href="#terms-catalogue">Catalogue &amp; prices</a>
            <a href="#terms-orders">Orders &amp; payment</a>
            <a href="#terms-rights">Content &amp; liability</a>
            <a href="#terms-contact">Changes &amp; contact</a>
          </nav>
        </aside>

        <article className="policy-content">
          <div className="policy-demo-note" role="note">
            <strong>Demo storefront notice</strong>
            <p>
              This website is a frontend prototype. It does not create a binding sale,
              capture payment, reserve stock, dispatch goods or establish a customer
              account. Do not enter real card or sensitive personal information.
            </p>
          </div>

          <section id="terms-demo">
            <p className="policy-section-number">01</p>
            <h2>Scope of these sample terms</h2>
            <p>
              These terms explain permitted use of the prototype presented under the
              {` ${siteConfig.brandName} `}brand configuration. They demonstrate the
              structure a commerce website may use, but they are not final legal terms
              and do not identify a contracting company.
            </p>
            <p>
              Production terms must be reviewed for the operator, markets, products,
              fulfilment model and applicable consumer law before a store accepts orders.
            </p>
          </section>

          <section id="terms-use">
            <p className="policy-section-number">02</p>
            <h2>Using the site responsibly</h2>
            <p>
              You may browse the catalogue, test cart interactions and use navigation
              for personal evaluation. You must not attempt to disrupt the site, probe
              systems without authorisation, introduce malicious code, scrape at a
              harmful rate or misuse content to mislead others.
            </p>
            <p>
              Users are responsible for their device, browser and internet connection.
              Access may be changed, suspended or withdrawn while this demo is developed.
            </p>
          </section>

          <section id="terms-catalogue">
            <p className="policy-section-number">03</p>
            <h2>Catalogue, colour &amp; sample pricing</h2>
            <p>
              Product names, descriptions, availability, reviews, prices and imagery
              are illustrative. Screen settings can also change perceived colour and
              scale. Measurements and material notes would need verification before a
              production launch.
            </p>
            <p>
              Prices are displayed in {siteConfig.currency.code} using the configured
              currency format. They do not represent a live offer and may not include
              destination-specific tax, duties or delivery. Errors may be corrected at
              any time in this prototype.
            </p>
          </section>

          <section id="terms-orders">
            <p className="policy-section-number">04</p>
            <h2>Cart, checkout, orders &amp; payment</h2>
            <p>
              Adding an item creates browser-local cart state only. Completing the demo
              checkout may display a confirmation screen, but no order reaches a seller,
              no inventory is allocated and no payment instruction is processed.
            </p>
            <p>
              A production store would need to explain offer and acceptance, payment
              authorisation, order cancellation, fraud checks, tax invoices and what
              happens when stock or pricing is incorrect. It would also need secure,
              secure hosted payment processing rather than collecting card data here.
            </p>
            <div className="policy-inline-links">
              <Link to="/shipping">Read sample shipping guidance</Link>
              <Link to="/returns">Read sample returns guidance</Link>
            </div>
          </section>

          <section id="terms-rights">
            <p className="policy-section-number">05</p>
            <h2>Content, third-party services &amp; liability</h2>
            <p>
              The prototype’s original interface, copy and visual assets are intended
              for this project. A production operator must secure the rights to every
              logo, font, image, product design and third-party integration it publishes.
              Brand names belonging to payment networks remain their owners’ property.
            </p>
            <p>
              External services can be unavailable or change independently. To the
              extent permitted by applicable law, this demo is provided for evaluation
              without a promise that it is uninterrupted, error-free or suitable for a
              real purchase. Nothing here excludes rights or liability that cannot
              legally be excluded.
            </p>
          </section>

          <section id="terms-contact">
            <p className="policy-section-number">06</p>
            <h2>Consumer rights, changes &amp; contact</h2>
            <p>
              Mandatory consumer rights always take priority over conflicting store
              terms. This prototype is presented from {siteConfig.contact.address}, but
              does not establish a governing-law or jurisdiction agreement. Production
              terms should name the legal operator and dispute process.
            </p>
            <p>
              Changes will be reflected by a revised date on this page. Questions about
              the prototype can be sent to the configured contact address below.
            </p>
            <div className="policy-contact-box">
              <div>
                <span>Questions about these sample terms?</span>
                <strong>{siteConfig.contact.email}</strong>
              </div>
              <Link to="/contact">Open the contact page</Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Terms;
